import cn from 'classnames';
import { useCallback, useMemo } from 'react';
import type { ReactNode } from 'react';

import { formatAmount, pagination } from '@webapp/common/lib/ui';
import { nop } from '@webapp/common/lib/utils';

import { Arrow } from '@webapp/ui/lib/icons';
import { CssUiComponent } from '@webapp/ui/lib/survey-custom';

import css from './pagination.css';

export const Page: VFC<{
    label: number | string;
    current: boolean;
    onSelect?: (page: number) => void;
    labelRenderer?: (label: number | string, element: ReactNode) => ReactNode;
}> = ({ current, label, labelRenderer, onSelect }) => {
    const inactive = typeof label === 'string' || current || (!onSelect && !labelRenderer);
    const onSelectClick = useCallback(() => onSelect(Number(label)), [onSelect, label]);

    const labelContent = useMemo(() => {
        const content = isNaN(Number(label)) ? label : formatAmount(label as number);

        if (labelRenderer) return labelRenderer(label, null);

        return content;
    }, [label, labelRenderer]);

    return (
        <div
            className={cn(css.num, { [css.current]: current, [css.inactive]: inactive })}
            onClick={inactive || !onSelect ? nop : onSelectClick}
        >
            {labelContent}
        </div>
    );
};

const Title: VFC = () => <span>Страница: </span>;

export const Pagination: VFC<{
    totalCount: number;
    limit: number;
    page: number;
    onPage?: (page: number) => void;
    labelRenderer?: (label: number | string, element: ReactNode, rel?: string) => ReactNode;
    className?: string;
    tail?: number;
}> = ({ className, labelRenderer, limit, onPage, page, tail = 5, totalCount }) => {
    const pagesCount = Math.ceil(totalCount / limit);
    const prevPage = Math.max(page - 1, 1);
    const onMinusClick = useCallback(() => onPage(prevPage), [onPage, prevPage]);
    const nextPage = Math.min(page + 1, pagesCount);
    const onPlusClick = useCallback(() => onPage(nextPage), [nextPage, onPage]);
    const firstPage = page === 1;
    const lastPage = page === pagesCount;

    const left = useMemo(() => {
        if (firstPage) return null;

        const click = labelRenderer ? null : onMinusClick;
        const arrow = <Arrow className={css.leftArrow} onClick={click} />;

        return labelRenderer ? labelRenderer(prevPage, arrow, 'prev') : arrow;
    }, [firstPage, labelRenderer, onMinusClick, prevPage]);

    const right = useMemo(() => {
        if (lastPage) return null;

        const click = labelRenderer ? null : onPlusClick;
        const arrow = <Arrow className={css.rightArrow} onClick={click} />;

        return labelRenderer ? labelRenderer(nextPage, arrow, 'next') : arrow;
    }, [labelRenderer, lastPage, nextPage, onPlusClick]);

    const pages = useMemo(
        () =>
            pagination(page, pagesCount, tail).map((p, idx) => (
                <Page current={p === page} key={idx} label={p} labelRenderer={labelRenderer} onSelect={onPage} />
            )),
        [labelRenderer, onPage, page, pagesCount, tail]
    );

    return (
        <div className={cn(css.pagination, className, CssUiComponent.NO_PRINT)}>
            <Title />
            {left}
            {pages}
            {right}
        </div>
    );
};
